import * as React from 'react'
import { startTransition } from 'react'
import loadable from '@loadable/component'

import { useTheme } from '@thg-commerce/gravity-elements/theme'
import { ZIndexLevel } from '@thg-commerce/gravity-theme'

import { CustomDropdown } from '../CustomDropdown/CustomDropdown'
import { DropdownOption } from '../FormItem'
import { VisuallyHidden } from '../VisuallyHidden'

import {
  DropdownContainer,
  NavigationButton,
  PaginationContent,
  PagniationContainer,
  StyledChevronLeft,
  StyledChevronRight,
  StyledDropdown,
  StyledLabel,
  Total,
} from './styles'

const SvgIcon = loadable(
  () => import('@thg-commerce/gravity-icons/src/components/SvgIcon'),
  { ssr: true, fallback: <div style={{ width: 24, height: 24 }} /> },
)

const MemoizedSvgIcon = React.memo(SvgIcon)

export interface PaginationProps {
  totalItems: number
  itemsPerPage: number
  activePage: number
  urlPrefix?: string
  onPageChange: (page: number) => void
  i18nText: {
    previous: string
    next: string
    page: string
    ofTotal: string
    dropdownLabel: string
    currentlyOnText: string
    ariaLabel: string
  }
  // Default alignment is right
  align?: 'left' | 'center' | 'right'
  className?: string
  trailingSlash: boolean
  maxDropdownHeight?: string
  useCustomDropdown?: boolean
  removeBlurFocus?: boolean
  width?: string
  stickyPosition?: boolean
}

const DEFAULT_MAX_DROPDOWN_HEIGHT = '248px'

const iconPlaceHolder = {
  xmlns: 'http://www.w3.org/2000/svg',
  viewBox: '',
  width: '',
  height: '',
  svgPath: '',
}

export const Pagination = (props: PaginationProps) => {
  const theme = useTheme()

  const { totalPages, nextPage, prevPage } = React.useMemo(() => {
    const total = Math.ceil(props.totalItems / props.itemsPerPage)
    const next = props.activePage + 1 <= total ? props.activePage + 1 : null
    const prev = props.activePage - 1 > 0 ? props.activePage - 1 : null

    return { totalPages: total, nextPage: next, prevPage: prev }
  }, [props.totalItems, props.itemsPerPage, props.activePage])

  const themeIcons = React.useMemo(
    () => ({
      hasLeftIcon: Boolean(
        theme.elements?.pagination?.chevronIcon?.left?.svgPath,
      ),
      hasRightIcon: Boolean(
        theme.elements?.pagination?.chevronIcon?.right?.svgPath,
      ),
      leftIconProps: theme.elements?.pagination?.chevronIcon?.left?.svgPath
        ? {
            xmlns: 'http://www.w3.org/2000/svg',
            viewBox: theme.elements.pagination.chevronIcon.viewBox,
            width: theme.elements.pagination.chevronIcon.width,
            height: theme.elements.pagination.chevronIcon.height,
            svgPath: theme.elements.pagination.chevronIcon.left.svgPath || '',
          }
        : iconPlaceHolder,
      rightIconProps: theme.elements?.pagination?.chevronIcon?.right?.svgPath
        ? {
            xmlns: 'http://www.w3.org/2000/svg',
            viewBox: theme.elements.pagination.chevronIcon.viewBox,
            width: theme.elements.pagination.chevronIcon.width,
            height: theme.elements.pagination.chevronIcon.height,
            svgPath: theme.elements.pagination.chevronIcon.right.svgPath,
          }
        : iconPlaceHolder,
    }),
    [theme.elements?.pagination?.chevronIcon],
  )

  const navigationUrls = React.useMemo(
    () => ({
      prevUrl:
        props.urlPrefix && prevPage !== null
          ? `${props.urlPrefix}${prevPage}${props.trailingSlash ? '/' : ''}`
          : undefined,
      nextUrl:
        props.urlPrefix && nextPage !== null
          ? `${props.urlPrefix}${nextPage}${props.trailingSlash ? '/' : ''}`
          : undefined,
    }),
    [props.urlPrefix, prevPage, nextPage, props.trailingSlash],
  )

  const i18nTexts = React.useMemo(
    () => ({
      dropdownAriaText: `${
        props.i18nText.dropdownLabel
      }, ${props.i18nText.currentlyOnText
        .replace('%s', props.activePage.toString())
        .replace('%s', totalPages.toString())}`,
      totalText: props.i18nText.ofTotal.replace('%s', totalPages.toString()),
    }),
    [props.i18nText, props.activePage, totalPages],
  )

  const handlePrevClick = React.useCallback(() => {
    if (!props.urlPrefix && prevPage !== null) {
      startTransition(() => {
        props.onPageChange(props.activePage - 1)
      })
    }
  }, [props.onPageChange, props.activePage, prevPage, props.urlPrefix])

  const handleNextClick = React.useCallback(() => {
    if (!props.urlPrefix && nextPage !== null) {
      startTransition(() => {
        props.onPageChange(props.activePage + 1)
      })
    }
  }, [props.onPageChange, props.activePage, nextPage, props.urlPrefix])

  const handleDropdownChange = React.useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const newPage = parseInt(event.target.value, undefined)
      startTransition(() => {
        props.onPageChange(newPage)
      })
    },
    [props.onPageChange],
  )

  const handleCustomDropdownChange = React.useCallback(
    (event) => {
      const newPage = parseInt(event.value, undefined)
      startTransition(() => {
        props.onPageChange(newPage)
      })
    },
    [props.onPageChange],
  )

  const pageOptions = React.useMemo(
    () =>
      Array(totalPages)
        .fill(' ')
        .map((_, index) => (
          <DropdownOption key={index} value={index + 1}>
            {index + 1}
          </DropdownOption>
        )),
    [totalPages],
  )

  const customDropdownPageOptions = React.useMemo(
    () =>
      Array(totalPages)
        .fill(' ')
        .map((_, index) => ({
          key: index + 1,
          value: (index + 1).toString(),
          displayText: (index + 1).toString(),
        })),
    [totalPages],
  )

  return (
    <PagniationContainer className={props.className} align={props.align}>
      <PaginationContent aria-label={props.i18nText.ariaLabel}>
        <NavigationButton
          as={props.urlPrefix ? `a` : `button`}
          disabled={prevPage === null}
          aria-disabled={prevPage === null}
          role={prevPage === null ? 'button' : undefined}
          emphasis="low"
          onClick={!props.urlPrefix ? handlePrevClick : undefined}
          href={navigationUrls.prevUrl}
          data-testid="previous-link"
        >
          {themeIcons.hasLeftIcon ? (
            <MemoizedSvgIcon
              xmlns={themeIcons.leftIconProps.xmlns}
              viewBox={themeIcons.leftIconProps.viewBox}
              width={themeIcons.leftIconProps.width}
              height={themeIcons.leftIconProps.height}
            >
              <path d={themeIcons.leftIconProps.svgPath} fillRule="evenodd" />
            </MemoizedSvgIcon>
          ) : (
            <StyledChevronLeft />
          )}
          {props.i18nText.previous}
          <VisuallyHidden text={props.i18nText.page} />
        </NavigationButton>

        {props.useCustomDropdown ? (
          <React.Fragment>
            <StyledLabel>
              <VisuallyHidden text={i18nTexts.dropdownAriaText} />
              <CustomDropdown
                data-testid="select-page-dropdown"
                aria-label={props.i18nText.dropdownLabel}
                onChange={handleCustomDropdownChange}
                options={customDropdownPageOptions}
                selected={props.activePage}
                width={props.width ?? '100%'}
                stickyPosition={props.stickyPosition}
                maxDropdownHeight={
                  props.maxDropdownHeight || DEFAULT_MAX_DROPDOWN_HEIGHT
                }
                removeBlurFocus={props.removeBlurFocus}
                zIndex={ZIndexLevel.Base5}
                iconOverride={
                  theme.elements?.pagination?.chevronIcon?.down?.svgPath
                    ? {
                        svgPath:
                          theme.elements.pagination.chevronIcon.down.svgPath,
                        viewBox: theme.elements.pagination.chevronIcon.viewBox,
                        width: theme.elements.pagination.chevronIcon.width,
                        height: theme.elements.pagination.chevronIcon.height,
                      }
                    : iconPlaceHolder
                }
              />
            </StyledLabel>
            <Total aria-hidden>{i18nTexts.totalText}</Total>
          </React.Fragment>
        ) : (
          <DropdownContainer>
            <label>
              <VisuallyHidden text={i18nTexts.dropdownAriaText} />
              <StyledDropdown
                label={props.i18nText.dropdownLabel}
                labelHidden
                onChange={handleDropdownChange}
                value={props.activePage.toString()}
              >
                {pageOptions}
              </StyledDropdown>
            </label>
            <Total aria-hidden>{i18nTexts.totalText}</Total>
          </DropdownContainer>
        )}

        <NavigationButton
          as={props.urlPrefix ? `a` : `button`}
          disabled={nextPage === null}
          aria-disabled={nextPage === null}
          role={nextPage === null ? 'button' : undefined}
          emphasis="low"
          onClick={!props.urlPrefix ? handleNextClick : undefined}
          href={navigationUrls.nextUrl}
          data-testid="next-link"
        >
          {props.i18nText.next} <VisuallyHidden text={props.i18nText.page} />
          {themeIcons.hasRightIcon ? (
            <MemoizedSvgIcon
              xmlns={themeIcons.rightIconProps.xmlns}
              viewBox={themeIcons.rightIconProps.viewBox}
              width={themeIcons.rightIconProps.width}
              height={themeIcons.rightIconProps.height}
            >
              <path d={themeIcons.rightIconProps.svgPath} fillRule="evenodd" />
            </MemoizedSvgIcon>
          ) : (
            <StyledChevronRight />
          )}
        </NavigationButton>
      </PaginationContent>
    </PagniationContainer>
  )
}
